/**
 * Cache Manager Service
 * 
 * Implements hybrid caching strategy:
 * - Memory cache (fastest, primary)
 * - LocalStorage cache (persists across refreshes, fallback)
 * - TTL-based expiration
 * - Request deduplication
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheManager {
  private memoryCache: Map<string, CacheEntry<any>> = new Map();
  private inFlightRequests: Map<string, Promise<any>> = new Map();
  private readonly STORAGE_PREFIX = 'gac_'; // GenAnything Cache prefix

  /**
   * Get data from cache (memory first, then localStorage)
   */
  get<T>(key: string): T | null {
    // Check memory cache first (fastest)
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && this.isValid(memoryEntry)) {
      return memoryEntry.data;
    }

    // Check localStorage (slower but persists)
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const entry: CacheEntry<T> = JSON.parse(stored);
        
        if (this.isValid(entry)) {
          // Promote to memory cache for faster subsequent access
          this.memoryCache.set(key, entry);
          return entry.data;
        } else {
          // Expired - clean up
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.warn(`⚠️ [Cache] LocalStorage read error for ${key}:`, error);
    }

    return null;
  }

  /**
   * Set data in cache (both memory and localStorage)
   */
  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
    };

    // Set in memory cache
    this.memoryCache.set(key, entry);

    // Set in localStorage
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      // localStorage might be full or disabled
      console.warn(`⚠️ [Cache] LocalStorage write error for ${key}:`, error);
    }
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(key: string): void {
    this.memoryCache.delete(key);
    
    try {
      const storageKey = this.STORAGE_PREFIX + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn(`⚠️ [Cache] LocalStorage delete error for ${key}:`, error);
    }
  }

  /**
   * Clear all caches
   */
  clear(): void {
    this.memoryCache.clear();
    
    try {
      // Clear all cache entries from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn(`⚠️ [Cache] LocalStorage clear error:`, error);
    }
  }

  /**
   * Check if cache entry is still valid
   */
  private isValid(entry: CacheEntry<any>): boolean {
    const age = Date.now() - entry.timestamp;
    return age < entry.ttl;
  }

  /**
   * Deduplicate concurrent requests for the same key
   * Returns existing promise if request is in flight
   */
  async deduplicate<T>(
    key: string,
    fetchFn: () => Promise<T>
  ): Promise<T> {
    // Check if request is already in flight
    const inFlight = this.inFlightRequests.get(key);
    if (inFlight) {
      return inFlight;
    }

    // Start new request
    const promise = fetchFn().finally(() => {
      // Clean up in-flight tracker
      this.inFlightRequests.delete(key);
    });

    this.inFlightRequests.set(key, promise);
    return promise;
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  getStats(): {
    memorySize: number;
    localStorageSize: number;
    keys: string[];
  } {
    const keys: string[] = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          keys.push(key.substring(this.STORAGE_PREFIX.length));
        }
      }
    } catch (error) {
      // Ignore
    }

    return {
      memorySize: this.memoryCache.size,
      localStorageSize: keys.length,
      keys,
    };
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Cache key constants
export const CACHE_KEYS = {
  SUBSCRIPTION_PLANS: 'subscription_plans',
  CREDIT_PACKS: 'credit_packs',
  USER_VIDEOS: 'user_assets_video',
  USER_IMAGES: 'user_assets_image',
  USER_ASSETS_ALL: 'user_assets_all',
  USER_DATA: 'user_data',
  CREDIT_BALANCE: 'credit_balance',
} as const;

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  SUBSCRIPTION_PLANS: 5 * 60 * 1000, // 5 minutes
  CREDIT_PACKS: 5 * 60 * 1000, // 5 minutes
  USER_VIDEOS: 30 * 1000, // 30 seconds
  USER_DATA: 60 * 1000, // 1 minute
  CREDIT_BALANCE: 30 * 1000, // 30 seconds
} as const;

